/**
 * Session expiry for the admin portal.
 *
 * A session ends after four hours without a request. The server enforces it
 * (Auth/authentication.py) and answers 401 with a `session_expired` code; this
 * is the one reaction to that — clear what is stored and go to the login page
 * with a note, so an admin is never left on a dashboard whose panels all fail
 * for no stated reason.
 *
 * The portal talks to the API through several axios instances, and an
 * interceptor on the default one does not cover those, so each is attached
 * explicitly. Anything else 401 is left to the caller, which may legitimately
 * be asking about something this admin cannot see.
 */
const LAST_SEEN_KEY = 'admin-last-seen';

/** Must match SESSION_TTL_HOURS on the server. */
export const SESSION_TTL_MS = 4 * 60 * 60 * 1000;

const TOUCH_INTERVAL_MS = 60 * 1000;
let lastTouch = 0;
let ending = false;

const safe = (fn, fallback) => {
  try { return fn(); } catch (e) { return fallback; }
};

export const touchSession = (force = false) => {
  const now = Date.now();
  if (!force && now - lastTouch < TOUCH_INTERVAL_MS) return;
  lastTouch = now;
  safe(() => localStorage.setItem(LAST_SEEN_KEY, String(now)));
};

export const endSession = () => {
  // One lapse sends one person to one login page, however many requests failed
  // together. The latch releases shortly after, so a later lapse can still act
  // if the navigation never happened.
  if (ending) return;
  ending = true;
  setTimeout(() => { ending = false; }, 2000);
  safe(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem(LAST_SEEN_KEY);
  });
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login?expired=1');
  }
};

export const isSessionExpiry = (error) => {
  if (error?.response?.status !== 401) return false;
  const data = error.response.data || {};
  const code = data.detail?.code || data.code;
  const detail = String(data.detail || '');
  return code === 'session_expired' || /session has expired/i.test(detail);
};

/** Attaches the guard to an axios instance (or the default export). */
export const attachSessionGuard = (instance) => {
  instance.interceptors.response.use(
    (response) => { touchSession(); return response; },
    (error) => {
      if (isSessionExpiry(error)) endSession();
      return Promise.reject(error);
    },
  );
  return instance;
};

/**
 * On load: a tab left open past the window lands on the login page rather than
 * on panels that all fail. The server remains the authority; this only avoids
 * the dead screen.
 */
export const checkStoredSession = () => {
  const token = safe(() => localStorage.getItem('token') || localStorage.getItem('authToken'), null);
  if (!token) return;
  const lastSeen = Number(safe(() => localStorage.getItem(LAST_SEEN_KEY), 0) || 0);
  if (!lastSeen) { touchSession(true); return; }
  if (Date.now() - lastSeen > SESSION_TTL_MS) endSession();
};
